import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  XMarkIcon,
  QrCodeIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Service, ServiceItem } from '../types';
import { proposalsApi, catalogApi } from '../services/api';
import ItemHistoryModal from './ItemHistoryModal';

interface ServiceItemManagementProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service;
  initialFilter?: string;
}

const STATUS_OPTIONS = [
  { value: 'available', label: 'Disponível', icon: CheckCircleIcon, color: 'text-green-600' },
  { value: 'rented', label: 'Locado', icon: ClockIcon, color: 'text-blue-600' },
  { value: 'maintenance', label: 'Manutenção', icon: WrenchScrewdriverIcon, color: 'text-yellow-600' },
  { value: 'damaged', label: 'Danificado', icon: ExclamationTriangleIcon, color: 'text-red-600' },
  { value: 'retired', label: 'Retirado', icon: XCircleIcon, color: 'text-gray-600' }
];

export default function ServiceItemManagement({ isOpen, onClose, service, initialFilter = 'all' }: ServiceItemManagementProps) {
  const [items, setItems] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>(initialFilter);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedItemForHistory, setSelectedItemForHistory] = useState<ServiceItem | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  useEffect(() => {
    if (isOpen && service.id) {
      setFilter(initialFilter);
      setSearchTerm('');
      fetchServiceItems();
    }
  }, [isOpen, service.id, initialFilter]);

  const fetchServiceItems = async () => {
    setLoading(true);
    try {
      if (initialFilter === 'maintenance' && service.track_individual_items) {
        // Use dedicated maintenance API for better performance when filtering by maintenance
        const response = await catalogApi.getServiceMaintenanceItems(service.id.toString());
        setItems(response.maintenance_items || []);
      } else {
        // Use general items API for all other filters
        const response = await proposalsApi.getServiceItems({ service: service.id });
        setItems(response.results || response || []);
      }
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
      setItems([]); // Set empty array if API fails
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    
    // If we're switching from maintenance-specific data to a different filter
    // we need to refetch all items
    if (initialFilter === 'maintenance' && newFilter !== 'maintenance') {
      setLoading(true);
      proposalsApi.getServiceItems({ service: service.id })
        .then(response => {
          setItems(response.results || response || []);
        })
        .catch(error => {
          console.error('Erro ao carregar itens:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const filteredItems = items.filter(item => {
    const matchesFilter = filter === 'all' || item.status === filter;
    const matchesSearch = !searchTerm || 
      item.internal_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.qr_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.current_location && item.current_location.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const handleStatusChange = async (itemId: string, newStatus: string, description?: string) => {
    try {
      await proposalsApi.changeServiceItemStatus(
        itemId,
        newStatus,
        description || `Status alterado para ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`
      );
      
      // Atualizar o item localmente
      setItems(items.map(item => 
        item.id === itemId ? { ...item, status: newStatus as any } : item
      ));
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Erro ao alterar status do item');
    }
  };

  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedItems.length === 0) {
      alert('Selecione pelo menos um item');
      return;
    }

    const description = `Status alterado em lote para ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`;
    
    try {
      await Promise.all(
        selectedItems.map(itemId => 
          proposalsApi.changeServiceItemStatus(itemId, newStatus, description)
        )
      );
      
      // Atualizar itens localmente
      setItems(items.map(item => 
        selectedItems.includes(item.id) ? { ...item, status: newStatus as any } : item
      ));
      
      setSelectedItems([]);
    } catch (error) {
      console.error('Erro ao alterar status em lote:', error);
      alert('Erro ao alterar status dos itens');
    }
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const getStatusInfo = (status: string) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const handleOpenHistory = (item: ServiceItem) => {
    setSelectedItemForHistory(item);
    setShowHistoryModal(true);
  };

  const handleCloseHistory = () => {
    setShowHistoryModal(false);
    setSelectedItemForHistory(null);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      Gerenciar Itens Individuais
                    </Dialog.Title>
                    <p className="text-xs text-gray-500 mt-1">
                      {service.name} - {items.length} itens total
                    </p>
                  </div>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Filtros e Ações em Lote */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Filtrar por status:</label>
                      <select
                        value={filter}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                      >
                        <option value="all">Todos ({items.length})</option>
                        {STATUS_OPTIONS.map(status => {
                          const count = items.filter(item => item.status === status.value).length;
                          return (
                            <option key={status.value} value={status.value}>
                              {status.label} ({count})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Buscar:</label>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Código, QR ou localização..."
                        className="border border-gray-300 rounded-md px-3 py-1 text-sm w-64"
                      />
                    </div>
                  </div>

                  {selectedItems.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {selectedItems.length} selecionado(s)
                      </span>
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleBulkStatusChange(e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                        defaultValue=""
                      >
                        <option value="">Alterar status...</option>
                        {STATUS_OPTIONS.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Lista de Itens */}
                <div className="max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-500 mt-2">Carregando itens...</p>
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {!service.track_individual_items ? (
                        <div>
                          <p className="mb-4">Este serviço não possui rastreamento individual de itens.</p>
                          <p className="text-sm">Para gerenciar itens individualmente, ative o rastreamento nas configurações do serviço.</p>
                        </div>
                      ) : (
                        filter === 'all' ? 'Nenhum item encontrado' : `Nenhum item com status "${STATUS_OPTIONS.find(s => s.value === filter)?.label}"`
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {filteredItems.map((item) => {
                        const statusInfo = getStatusInfo(item.status);
                        const StatusIcon = statusInfo.icon;
                        
                        return (
                          <div
                            key={item.id}
                            className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                              selectedItems.includes(item.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.includes(item.id)}
                                  onChange={() => toggleItemSelection(item.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <QrCodeIcon className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="font-medium text-gray-900">{item.internal_code}</p>
                                  <p className="text-xs text-gray-500">{item.qr_code}</p>
                                </div>
                              </div>
                              <div className={`flex items-center gap-1 ${statusInfo.color}`}>
                                <StatusIcon className="h-4 w-4" />
                                <span className="text-xs font-medium">{statusInfo.label}</span>
                              </div>
                            </div>

                            {item.current_location && (
                              <p className="text-xs text-gray-600 mb-2">
                                📍 {item.current_location}
                              </p>
                            )}

                            {item.condition_notes && (
                              <p className="text-xs text-gray-600 mb-3">
                                💬 {item.condition_notes}
                              </p>
                            )}

                            <div className="space-y-2">
                              <select
                                value={item.status}
                                onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                              >
                                {STATUS_OPTIONS.map(status => (
                                  <option key={status.value} value={status.value}>
                                    {status.label}
                                  </option>
                                ))}
                              </select>
                              
                              <button
                                onClick={() => handleOpenHistory(item)}
                                className="w-full inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                title="Ver histórico completo do item"
                              >
                                <DocumentTextIcon className="h-3 w-3 mr-1" />
                                Histórico
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Rodapé */}
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={onClose}
                  >
                    Fechar
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

        {/* Modal de Histórico do Item */}
        {selectedItemForHistory && (
          <ItemHistoryModal
            isOpen={showHistoryModal}
            onClose={handleCloseHistory}
            item={selectedItemForHistory}
          />
        )}
      </Dialog>
    </Transition>
  );
}